import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { complianceService } from '../../services/api';

const propertySchema = z.object({
    apartment: z.string().optional(),
    street: z.string().optional(),
    city: z.string().optional(),
    county: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    telephone: z.string().optional(),
    propertyType: z.string().optional(),
    categoryPropertyType: z.string().optional(),
});

export type PropertyFormData = z.infer<typeof propertySchema>;

export interface CategoryOption { value: string; label: string }

// Derive the legacy property `type` (used across PropertyDetail / Edit modal)
// from the Category Property Type selected during add-property, so the choice
// carries through to the property card instead of defaulting to 'Residential'.
const RESIDENTIAL_CATEGORY_CODES = new Set(['A', 'B', 'C1', 'C2', 'D1', 'D2', 'D2b', 'E', 'O', 'M1']);

export function categoryCodeToType(categoryCode?: string): string {
    if (!categoryCode) return 'Residential';
    return RESIDENTIAL_CATEGORY_CODES.has(categoryCode) ? 'Residential' : 'Commercial';
}

export function categoryLabel(categoryCode?: string): string | null {
    if (!categoryCode) return null;
    const match = CATEGORY_OPTIONS.find((opt) => opt.value === categoryCode);
    return match ? match.label : null;
}

interface PropertyFormProps {
    initialData?: any;
    onSuccess: (propertyId: string, data: any) => void;
    onCancel: () => void;
    onLimitReached?: (tierName: string, current: number, limit: number) => void;
}

const CATEGORY_OPTIONS = [
    { value: '', label: 'Select Category Property Type' },
    { value: 'A', label: 'A Real Property: Single-family Residential' },
    { value: 'B', label: 'B Real Property: Multifamily Residential' },
    { value: 'C1', label: 'C1 Real Property: Vacant Lots and Land Tracts' },
    { value: 'C2', label: 'C2 Real Property: Colonia Lots and Land Tracts' },
    { value: 'D1', label: 'D1 Real Property: Qualified Open-space Land' },
    { value: 'D2', label: 'D2 Real Property: Farm and Ranch' },
    { value: 'D2b', label: 'Improvements on Qualified Open-Space Land' },
    { value: 'E', label: 'E Real Property: Rural Land, not qualified for open-space land appraisal, and Improvements' },
    { value: 'F1', label: 'F1 Real Property: Commercial' },
    { value: 'F2', label: 'F2 Real Property: Industrial and Manufacturing' },
    { value: 'G1', label: 'G1 Real Property: Oil and Gas' },
    { value: 'G2', label: 'G2 Real Property: Minerals Category Property Type' },
    { value: 'G3', label: 'G3 Real Property: Other Sub-surface Interests in Land' },
    { value: 'H1', label: 'H1 Tangible Personal Property: Personal Vehicles, not used for business purposes' },
    { value: 'H2', label: 'H2 Tangible Personal Property: Goods in Transit' },
    { value: 'J', label: 'J Real and Tangible Personal Property: Utilities' },
    { value: 'L1', label: 'L1 Personal Property: Commercial' },
    { value: 'L2', label: 'L2 Personal Property: Industrial and Manufacturing' },
    { value: 'M1', label: 'M1 Mobile Homes' },
    { value: 'M2', label: 'M2 Other Tangible Personal Property' },
    { value: 'N', label: 'N Intangible Personal Property Only' },
    { value: 'O', label: 'O Real Property: Residential Inventory' },
    { value: 'S', label: 'S Special Inventory' },
    { value: 'X', label: 'X Totally Exempt Property and subcategories' },
];

export default function PropertyForm({ initialData, onSuccess, onCancel, onLimitReached }: PropertyFormProps) {
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm<PropertyFormData>({
        resolver: zodResolver(propertySchema),
        defaultValues: initialData ? {
            apartment: initialData.apartment || '',
            street: initialData.street || '',
            city: initialData.city || '',
            county: initialData.county || '',
            state: initialData.state || '',
            zipCode: initialData.zipCode || '',
            telephone: initialData.telephone || '',
            propertyType: initialData.propertyType || '',
            categoryPropertyType: initialData.categoryPropertyType || '',
        } : {},
    });

    const onSubmit = async (data: PropertyFormData) => {
        setSubmitError(null);
        try {
            const payload: any = { ...data };
            if (initialData?.id) {
                if (data.categoryPropertyType) payload.type = categoryCodeToType(data.categoryPropertyType);
            } else {
                payload.type = categoryCodeToType(data.categoryPropertyType);
            }
            console.log('[PropertyForm] Submitting property:', payload);
            let response;
            if (initialData?.id) {
                response = await complianceService.updateProperty(initialData.id, payload);
                console.log('[PropertyForm] Update Response:', response);
                onSuccess(initialData.id, payload);
            } else {
                response = await complianceService.addProperty(payload);
                console.log('[PropertyForm] Create Response:', response);
                const payload2 = response.data?.data || response.data || {};
                const propertyId = payload2.id || payload2._id;
                if (!propertyId) {
                    console.error('[PropertyForm] Could not extract propertyId from response', response.data);
                    setSubmitError('Property created but the server returned an unexpected response shape.');
                    return;
                }
                onSuccess(propertyId, payload);
            }
        } catch (err: any) {
            console.error('[PropertyForm] Error:', {
                status: err.response?.status,
                statusText: err.response?.statusText,
                data: err.response?.data,
            });
            const backendMessage = err.response?.data?.message || err.response?.data?.error;
            if (err.response?.status === 403 && err.response?.data?.code === 'LIMIT_REACHED') {
                const tierName = err.response?.data?.tierName || 'your current plan';
                const current = err.response?.data?.current || 1;
                const limit = err.response?.data?.limit || 1;
                if (onLimitReached) {
                    onLimitReached(tierName, current, limit);
                } else {
                    setSubmitError(`You've reached the property limit (${current}/${limit}). Please upgrade.`);
                }
            } else if (err.response?.status === 401) {
                setSubmitError("Session expired. Please log in again.");
            } else if (backendMessage) {
                setSubmitError(`Error: ${backendMessage}`);
            } else {
                setSubmitError("Failed to add property. Please try again.");
            }
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-medium text-gray-900">Step 1: Property Details</h3>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 md:p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Apartment</label>
                        <input
                            type="text"
                            {...register('apartment')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="Apt 4B"
                        />
                        {errors.apartment && <p className="mt-1 text-sm text-red-600">{errors.apartment.message}</p>}
                    </div>
                    <div className="col-span-1 md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
                        <input
                            type="text"
                            {...register('street')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="123 Example Street"
                        />
                        {errors.street && <p className="mt-1 text-sm text-red-600">{errors.street.message}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                            type="text"
                            {...register('city')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="Austin"
                        />
                        {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                        <input
                            type="text"
                            {...register('county')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="Travis County"
                        />
                        {errors.county && <p className="mt-1 text-sm text-red-600">{errors.county.message}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                            type="text"
                            {...register('state')}
                            maxLength={2}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="TX"
                        />
                        {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                        <input
                            type="text"
                            {...register('zipCode')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="78701"
                        />
                        {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode.message}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category Property Type</label>
                        <select
                            {...register('categoryPropertyType')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                        >
                            {CATEGORY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Telephone</label>
                        <input
                            type="text"
                            {...register('telephone')}
                            className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                {submitError && (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <i className="fas fa-exclamation-circle text-red-400 mt-0.5 mr-3"></i>
                            <div className="text-sm text-red-700">{submitError}</div>
                        </div>
                    </div>
                )}

                <div className="pt-5 flex justify-end items-center space-x-3 border-t border-gray-100">
                    <button
                        type="button"
                        className="bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin mr-2"></i> Creating...
                            </>
                        ) : (
                            'Next: Upload Certificates'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
